import React, { useEffect } from 'react';
import type { BodyProps } from '../types';
import { useAuditStore } from '../stores/auditStore';
import { OverviewPage } from '../pages/OverviewPage';
import { IssueListingPage } from '../pages/IssueListingPage';
import { ElementListingPage } from '../pages/ElementListingPage';
import { AccessibilityChecker } from './AccessibilityChecker';
import { OverviewBackground } from './icons/OverviewBackground';

export const Body: React.FC<BodyProps> = ({ currentPage, onNavigate, currentUrl }) => {
  const selectedIssueCategory = useAuditStore((s) => s.selectedIssueCategory);
  const auditInProgress = useAuditStore((s) => s.auditInProgress);

  // Expose a simple navigate helper on window for inner items that only have click handlers
  (window as any).webyesNavigate = onNavigate;
  
  // Clear highlights when page changes
  useEffect(() => {
    const clearHighlights = () => {
      try {
        const highlightUtils = (window as any).webyesHighlightUtils;  
        if (highlightUtils?.clearAllHighlights) {
          highlightUtils.clearAllHighlights();
        }
      } catch (error) {
        console.warn('Highlight utils not available in this context');
      }
    };
    
    clearHighlights();
  }, [currentPage]);

  const getPageTitle = () => {
    switch (currentPage) {
      case 'accessibilityIssueHeading':
        return 'Accessibility checker';
      case 'accessibilityIssueDetails':
        return selectedIssueCategory === 'best-practice' ? 'Best practice details' : 'Issue details';
      case 'overview':
      default:
        return null;
    }
  };

  const renderPageHeader = () => {
    // Do not show header when analyzing (e.g. device switch) so UI matches initial load
    const isIssuePage = currentPage === 'accessibilityIssueHeading' || currentPage === 'accessibilityIssueDetails';
    if (isIssuePage && auditInProgress) return null;

    const title = getPageTitle();
    if (!title) return null;

    // Only show back button on Element Listing page (accessibilityIssueDetails)
    // Don't show back button on Issue Listing page (accessibilityIssueHeading)
    const showBackButton = currentPage === 'accessibilityIssueDetails';

    return (
      <div className="cy:mb-0 cy:bg-white cy:text-black cy:px-4 cy:py-3 cy:flex cy:items-center cy:gap-2">
        {showBackButton && (
          <button
            className="cy:border-0 cy:bg-transparent cy:text-black cy:cursor-pointer cy:flex cy:items-center cy:justify-center cy:p-1 cy:hover:bg-gray-50 cy:rounded"
            onClick={() => onNavigate('accessibilityIssueHeading')}
            aria-label="Go back to issues"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <h2 className="cy:m-0 cy:text-black cy:text-base cy:font-semibold">{title}</h2>
      </div>
    );
  };

  const renderPage = () => {
    const commonProps = { onNavigate, currentUrl };
    const isIssuePage = currentPage === 'accessibilityIssueHeading' || currentPage === 'accessibilityIssueDetails';

    // When on issue list or issue details and a device-switch audit is running, show analyzing loader
    // Use same layout as OverviewPage so the card position matches the first analysis page (top-aligned, no justify-center)
    if (isIssuePage && auditInProgress) {
      return (
        <div className="cy:relative cy:flex cy:flex-col cy:gap-6 cy:px-4 cy:py-6 cy:min-h-[500px]">
          <div className="cy:relative cy:z-10">
            <AccessibilityChecker
              onAnalyze={() => {}}
              isAnalyzing={true}
              auditResult={null}
            />
          </div>
          <div className="cy:flex cy:items-center cy:justify-center cy:pointer-events-none">
            <OverviewBackground width={400} height={400} />
          </div>
        </div>
      );
    }

    switch (currentPage) {
      case 'overview':
        return <OverviewPage {...commonProps} />;
      case 'accessibilityIssueHeading':
        return <IssueListingPage {...commonProps} />;
      case 'accessibilityIssueDetails':
        return <ElementListingPage {...commonProps} />;
      default:
        return <OverviewPage {...commonProps} />;
    }
  };

  return (
    <div className="cy:flex cy:flex-col cy:h-full">
      {/* Render header at Body level for connected design */}
      {renderPageHeader()}
      
      {/* Content area that fills remaining space */}
      <div className="cy:flex-1 cy:overflow-y-auto cy:min-h-0" style={{ backgroundColor: '#DCE3EE' }}>
        <div className="cy:h-full">
          {/* Render page content */}
          {renderPage()}
        </div>
      </div>
    </div>
  );
};

